import { z } from "zod";
export const EXTERNAL_CONVERSATION_MAX_TEXT_CHARS = 16_000;
export const EXTERNAL_CONVERSATION_ENVELOPE_TTL_MS = 5 * 60 * 1_000;
export const EXTERNAL_THREAD_CONTEXT_MAX_MESSAGES = 40;
export const EXTERNAL_THREAD_CONTEXT_MAX_MESSAGE_CODE_POINTS = 2_000;
export const EXTERNAL_THREAD_CONTEXT_MAX_CODE_POINTS = 10_000;
const externalIdentifierSchema = z.string().trim().min(1).max(1_024);
export const externalConversationClientSchema = z.enum(["slack", "teams"]);
export const externalConversationScopeSchema = z.enum([
    "personal",
    "group",
    "channel",
]);
export const externalConversationInvocationSchema = z.literal("explicit_mention");
const externalThreadContextMessageSchema = z.object({
    providerMessageId: externalIdentifierSchema,
    providerUserId: externalIdentifierSchema,
    providerUserName: z.string().trim().min(1).max(256).optional(),
    isBot: z.boolean(),
    sentAt: z.string().datetime({ offset: true }),
    text: z.string().trim().min(1),
}).strict().superRefine((message, context) => {
    if (Array.from(message.text).length > EXTERNAL_THREAD_CONTEXT_MAX_MESSAGE_CODE_POINTS) {
        context.addIssue({
            code: "custom",
            path: ["text"],
            message: "External thread context messages must be bounded",
        });
    }
});
export const externalThreadContextSchema = z.object({
    kind: z.literal("thread_history_v1"),
    truncated: z.boolean(),
    messages: z.array(externalThreadContextMessageSchema)
        .min(1)
        .max(EXTERNAL_THREAD_CONTEXT_MAX_MESSAGES),
}).strict().superRefine((threadContext, context) => {
    const seenMessageIds = new Set();
    let previousSentAt = Number.NEGATIVE_INFINITY;
    let totalCodePoints = 0;
    threadContext.messages.forEach((message, index) => {
        if (seenMessageIds.has(message.providerMessageId)) {
            context.addIssue({
                code: "custom",
                path: ["messages", index, "providerMessageId"],
                message: "External thread context message IDs must be unique",
            });
        }
        seenMessageIds.add(message.providerMessageId);
        const sentAt = Date.parse(message.sentAt);
        if (sentAt < previousSentAt) {
            context.addIssue({
                code: "custom",
                path: ["messages", index, "sentAt"],
                message: "External thread context messages must be chronological",
            });
        }
        previousSentAt = sentAt;
        totalCodePoints += Array.from(message.text).length;
    });
    if (totalCodePoints > EXTERNAL_THREAD_CONTEXT_MAX_CODE_POINTS) {
        context.addIssue({
            code: "custom",
            path: ["messages"],
            message: "External thread context must be bounded",
        });
    }
});
export const externalConversationEnvelopePayloadSchema = z.object({
    v: z.literal(1),
    aud: z.literal("mlx-external-conversation"),
    jti: z.string().trim().min(9).max(128),
    iat: z.number().int().nonnegative(),
    exp: z.number().int().nonnegative(),
    gatewayInstanceId: z.string().trim().min(1).max(128),
    client: externalConversationClientSchema,
    /* Finer-grained surface within the client — a Teams message can arrive
     * through Microsoft 365 Copilot; runs record it as their source. */
    surface: z.enum(["copilot"]).optional(),
    providerTenantId: externalIdentifierSchema,
    providerWorkspaceId: externalIdentifierSchema,
    providerUserId: externalIdentifierSchema,
    providerUserName: z.string().trim().min(1).max(256).optional(),
    providerConversationId: externalIdentifierSchema,
    /* Display only, like providerUserName: the shared channel's name, for the link an admin follows. */
    providerConversationName: z.string().trim().min(1).max(256).optional(),
    providerEventId: externalIdentifierSchema,
    providerMessageId: externalIdentifierSchema,
    providerThreadId: externalIdentifierSchema.optional(),
    providerReplyToMessageId: externalIdentifierSchema.optional(),
    providerRootMessageId: externalIdentifierSchema.optional(),
    scope: externalConversationScopeSchema,
    /* Slack Connect: the conversation is shared with another organisation, so it
     * runs only through an organisation admin's link to one MLX channel. */
    externallyShared: z.literal(true).optional(),
    text: z.string().trim().min(1).max(EXTERNAL_CONVERSATION_MAX_TEXT_CHARS),
    invocation: externalConversationInvocationSchema.optional(),
    threadContext: externalThreadContextSchema.optional(),
}).strict().superRefine((payload, context) => {
    if (payload.exp <= payload.iat ||
        payload.exp - payload.iat > EXTERNAL_CONVERSATION_ENVELOPE_TTL_MS) {
        context.addIssue({
            code: "custom",
            path: ["exp"],
            message: "External conversation envelopes must have a bounded positive lifetime",
        });
    }
    if (payload.scope !== "personal" && !payload.providerRootMessageId) {
        context.addIssue({
            code: "custom",
            path: ["providerRootMessageId"],
            message: "Group and channel conversations require an exact root message",
        });
    }
    if (payload.externallyShared && (payload.client !== "slack" || payload.scope === "personal")) {
        context.addIssue({
            code: "custom",
            path: ["externallyShared"],
            message: "Only a Slack channel or group conversation is marked as shared with another organisation",
        });
    }
    if (payload.invocation && payload.client !== "slack") {
        context.addIssue({
            code: "custom",
            path: ["invocation"],
            message: "Explicit mention invocation is supported only for Slack",
        });
    }
    if (payload.threadContext) {
        if (payload.client !== "slack" ||
            payload.invocation !== "explicit_mention" ||
            payload.scope === "personal" ||
            !payload.providerReplyToMessageId ||
            !payload.providerRootMessageId) {
            context.addIssue({
                code: "custom",
                path: ["threadContext"],
                message: "Thread context requires an explicit non-personal Slack thread mention",
            });
        }
        if (payload.providerReplyToMessageId !== payload.providerRootMessageId) {
            context.addIssue({
                code: "custom",
                path: ["providerReplyToMessageId"],
                message: "Slack thread context must use the exact parent root",
            });
        }
        const invocationTimestamp = Number(payload.providerMessageId);
        if (!Number.isFinite(invocationTimestamp) ||
            payload.threadContext.messages.some((message) => {
                const messageTimestamp = Number(message.providerMessageId);
                return !Number.isFinite(messageTimestamp) || messageTimestamp >= invocationTimestamp;
            })) {
            context.addIssue({
                code: "custom",
                path: ["threadContext", "messages"],
                message: "Thread context messages must precede the Slack invocation",
            });
        }
        if (payload.threadContext.messages.some((message) => message.providerMessageId === payload.providerMessageId)) {
            context.addIssue({
                code: "custom",
                path: ["threadContext", "messages"],
                message: "Thread context must exclude the invocation message",
            });
        }
    }
});
export const externalConversationIngressRequestSchema = z.object({
    envelope: z.string().trim().min(1).max(64_000),
}).strict();
export const externalConversationRejectionCodeSchema = z.enum([
    "unlinked_provider_tenant",
    "unlinked_provider_user",
    "unsupported_conversation",
    "notification_replies_disabled",
    "notification_reply_invalid",
    "notification_reply_no_access",
    "access_denied",
    "thread_context_required",
    "thread_message_ignored",
    "thread_write_denied",
    "shared_channel_unlinked",
    "channel_binding_changed",
    "processing_failed",
]);
const externalConversationAcceptedResponseSchema = z.object({
    accepted: z.literal(true),
    taskId: z.string().trim().min(1).max(128),
    runId: z.string().trim().min(1).max(128),
    sessionId: z.string().trim().min(1).max(128).optional(),
    threadOwnerProviderUserId: z.string().trim().min(1).max(256).optional(),
    sessionUrl: z.string().trim().url().optional(),
    deduped: z.boolean(),
}).strict();
const externalConversationRejectedResponseSchema = z.object({
    accepted: z.literal(false),
    code: externalConversationRejectionCodeSchema,
    /* For shared_channel_unlinked: where an organisation admin links the channel. */
    linkUrl: z.string().trim().url().optional(),
}).strict();
export const externalConversationIngressResponseSchema = z.discriminatedUnion("accepted", [
    externalConversationAcceptedResponseSchema,
    externalConversationRejectedResponseSchema,
]);
